import React, { useState } from 'react';
import { PROCEDURE_CATEGORIES, PRICE_UNITS } from '../constants';
import { cn } from '../../../utils/cn';
import { parseBulkInput } from '../../../utils/bulkEntryUtils';

const Procedures = ({ initialProcedures, providers, onContinue, onSkip, onBack, isEditMode = false }) => {
  const [procedures, setProcedures] = useState(
    initialProcedures && initialProcedures.length > 0
      ? initialProcedures.map(p => ({
          ...p,
          // Ensure price fields are strings for form inputs
          priceMin: p.priceMin !== null && p.priceMin !== undefined ? String(p.priceMin) : '',
          priceMax: p.priceMax !== null && p.priceMax !== undefined ? String(p.priceMax) : '',
          averagePrice: p.averagePrice !== null && p.averagePrice !== undefined ? String(p.averagePrice) : '',
        }))
      : [{
          procedureName: '',
          category: '',
          priceMin: '',
          priceMax: '',
          unit: '',
          averagePrice: '',
          providerNames: []
        }]
  );
  const [entryMode, setEntryMode] = useState('individual'); // 'individual' or 'bulk'
  const [bulkInput, setBulkInput] = useState('');

  // Sync with initialProcedures when they change (e.g., when loading existing clinic data)
  React.useEffect(() => {
    if (initialProcedures && initialProcedures.length > 0) {
      setProcedures(initialProcedures.map(p => ({
        ...p,
        // Ensure price fields are strings for form inputs
        priceMin: p.priceMin !== null && p.priceMin !== undefined ? String(p.priceMin) : '',
        priceMax: p.priceMax !== null && p.priceMax !== undefined ? String(p.priceMax) : '',
        averagePrice: p.averagePrice !== null && p.averagePrice !== undefined ? String(p.averagePrice) : '',
      })));
    }
  }, [initialProcedures]);

  const addProcedure = () => {
    setProcedures([...procedures, {
      procedureName: '',
      category: '',
      priceMin: '',
      priceMax: '',
      unit: '',
      averagePrice: '',
      providerNames: []
    }]);
  };

  const removeProcedure = (index) => {
    if (procedures.length > 1) {
      setProcedures(procedures.filter((_, i) => i !== index));
    }
  };

  const updateProcedure = (index, field, value) => {
    const updated = [...procedures];
    updated[index][field] = value;
    
    // Auto-calculate average if priceMin and priceMax are set but averagePrice isn't
    if ((field === 'priceMin' || field === 'priceMax') && !updated[index].averagePrice) {
      const min = parseFloat(updated[index].priceMin) || 0;
      const max = parseFloat(updated[index].priceMax) || 0;
      if (min > 0 && max > 0) {
        updated[index].averagePrice = ((min + max) / 2).toString();
      }
    }
    
    setProcedures(updated);
  };

  const toggleProvider = (procedureIndex, providerName) => {
    const updated = [...procedures];
    const providerNames = updated[procedureIndex].providerNames || [];
    
    if (providerNames.includes(providerName)) {
      updated[procedureIndex].providerNames = providerNames.filter(p => p !== providerName);
    } else {
      updated[procedureIndex].providerNames = [...providerNames, providerName];
    }
    
    setProcedures(updated);
  };

  const handleContinue = () => {
    // Filter out empty procedures and format data
    // Note: Allow procedures with just procedureName (bulk-added items have empty category initially)
    const filledProcedures = procedures
      .filter(p => p.procedureName.trim())
      .map(p => ({
        ...p,
        priceMin: p.priceMin ? parseFloat(p.priceMin) : null,
        priceMax: p.priceMax ? parseFloat(p.priceMax) : null,
        averagePrice: p.averagePrice ? parseFloat(p.averagePrice) : null,
      }));
    onContinue(filledProcedures);
  };

  const handleSkip = () => {
    onSkip([]);
  };

  // Bulk entry functions
  const parsedProcedures = React.useMemo(() => {
    if (!bulkInput.trim()) return [];
    return parseBulkInput(bulkInput);
  }, [bulkInput]);

  const handleBulkAdd = () => {
    if (parsedProcedures.length === 0) return;
    
    const newProcedures = parsedProcedures.map(name => ({
      procedureName: name,
      category: '',
      priceMin: '',
      priceMax: '',
      unit: '',
      averagePrice: '',
      providerNames: []
    }));
    
    // Filter out duplicates by name (case-insensitive)
    const existingNames = new Set(procedures.map(p => p.procedureName.toLowerCase()));
    const uniqueNewProcedures = newProcedures.filter(p => 
      !existingNames.has(p.procedureName.toLowerCase())
    );
    
    setProcedures([...procedures, ...uniqueNewProcedures]);
    setBulkInput('');
    setEntryMode('individual');
  };

  const handleBulkReplace = () => {
    if (parsedProcedures.length === 0) return;
    
    const newProcedures = parsedProcedures.map(name => ({
      procedureName: name,
      category: '',
      priceMin: '',
      priceMax: '',
      unit: '',
      averagePrice: '',
      providerNames: []
    }));
    
    setProcedures(newProcedures);
    setBulkInput('');
    setEntryMode('individual');
  };

  return (
    <div className="max-w-3xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center text-text hover:text-dark mb-6 transition-colors"
      >
        <span className="mr-2">←</span> Back
      </button>

      <h2 className="text-3xl font-bold mb-6">
        {isEditMode ? 'Edit Procedures' : 'Procedures Offered'}
      </h2>
      
      {isEditMode && procedures.length > 0 && procedures[0].procedureName ? (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            Review and edit the existing procedures below. You can add new procedures, update pricing, or remove existing ones.
          </p>
        </div>
      ) : (
        <p className="text-text mb-6">
          Add procedures and their pricing. You can skip this step.
        </p>
      )}

      {/* Entry Mode Toggle */}
      <div className="mb-6 flex items-center justify-between p-4 bg-gray-50 border border-border rounded-lg">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-text">Entry Mode:</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEntryMode('individual')}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                entryMode === 'individual'
                  ? 'bg-primary text-white'
                  : 'bg-white text-text border border-border hover:bg-gray-50'
              )}
            >
              Individual Entry
            </button>
            <button
              type="button"
              onClick={() => setEntryMode('bulk')}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                entryMode === 'bulk'
                  ? 'bg-primary text-white'
                  : 'bg-white text-text border border-border hover:bg-gray-50'
              )}
            >
              Bulk Entry
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Entry Mode */}
      {entryMode === 'bulk' && (
        <div className="mb-6 p-4 border border-border rounded-lg bg-blue-50">
          <label className="block text-sm font-medium mb-2">
            Paste Procedure Names (semicolon-separated)
          </label>
          <textarea
            value={bulkInput}
            onChange={(e) => setBulkInput(e.target.value)}
            placeholder="Botox; Dermal Fillers; Chemical Peel; Laser Hair Removal; Microneedling"
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:border-primary mb-4 font-mono text-sm"
            rows={6}
          />
          
          {parsedProcedures.length > 0 && (
            <div className="mb-4 p-3 bg-white border border-border rounded-lg">
              <p className="text-sm font-medium text-text mb-2">
                Preview: {parsedProcedures.length} procedure{parsedProcedures.length !== 1 ? 's' : ''} will be added
              </p>
              <p className="text-xs text-gray-600 mb-2">
                Note: You'll need to fill in category and pricing for each procedure after adding them.
              </p>
              <div className="max-h-32 overflow-y-auto">
                <ul className="list-disc list-inside text-sm text-text space-y-1">
                  {parsedProcedures.map((name, idx) => (
                    <li key={idx}>{name}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleBulkAdd}
              disabled={parsedProcedures.length === 0}
              className={cn(
                'px-4 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 transition-all font-medium text-sm',
                parsedProcedures.length === 0 && 'opacity-50 cursor-not-allowed'
              )}
            >
              Add to List ({parsedProcedures.length})
            </button>
            <button
              type="button"
              onClick={handleBulkReplace}
              disabled={parsedProcedures.length === 0}
              className={cn(
                'px-4 py-2 bg-white border border-border text-text rounded-lg hover:bg-gray-50 transition-all font-medium text-sm',
                parsedProcedures.length === 0 && 'opacity-50 cursor-not-allowed'
              )}
            >
              Replace All ({parsedProcedures.length})
            </button>
            <button
              type="button"
              onClick={() => {
                setBulkInput('');
                setEntryMode('individual');
              }}
              className="px-4 py-2 bg-white border border-border text-text rounded-lg hover:bg-gray-50 transition-all font-medium text-sm ml-auto"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Individual Entry Mode */}
      {entryMode === 'individual' && (
        <>
      <div className="space-y-6 mb-6">
        {procedures.map((procedure, index) => {
          // Check if this is an existing procedure (loaded from API)
          const isExistingProcedure = !!procedure.procedureId;
          
          return (
          <div
            key={index}
            className="p-6 border border-border rounded-lg relative"
          >
            {/* Existing badge */}
            {isExistingProcedure && (
              <div className="absolute -top-2 -right-2 bg-blue-500 text-white px-2 py-0.5 rounded text-xs font-semibold shadow-sm">
                Existing
              </div>
            )}
            
            {/* Procedure Name & Category */}
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Procedure Name {index === 0 && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  value={procedure.procedureName}
                  onChange={(e) => updateProcedure(index, 'procedureName', e.target.value)}
                  placeholder="Botox"
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Category {index === 0 && <span className="text-red-500">*</span>}
                </label>
                <select
                  value={procedure.category}
                  onChange={(e) => updateProcedure(index, 'category', e.target.value)}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:border-primary"
                >
                  <option value="">Select Category</option>
                  {PROCEDURE_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Price Range & Unit */}
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Price Min
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={procedure.priceMin}
                  onChange={(e) => updateProcedure(index, 'priceMin', e.target.value)}
                  placeholder="12"
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Price Max
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={procedure.priceMax}
                  onChange={(e) => updateProcedure(index, 'priceMax', e.target.value)}
                  placeholder="15"
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Unit
                </label>
                <select
                  value={procedure.unit}
                  onChange={(e) => updateProcedure(index, 'unit', e.target.value)}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:border-primary"
                >
                  {PRICE_UNITS.map((unit) => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Average Price */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Average Price (optional)
              </label>
              <input
                type="number"
                step="0.01"
                value={procedure.averagePrice}
                onChange={(e) => updateProcedure(index, 'averagePrice', e.target.value)}
                placeholder="13.50"
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:border-primary"
              />
              <p className="text-xs text-text mt-1">
                If not provided, we'll calculate from price range
              </p>
            </div>

            {/* Provider Selection */}
            {providers && providers.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Provider(s)
                </label>
                <div className="space-y-2">
                  {providers.map((provider, providerIndex) => (
                    provider.providerName && (
                      <label
                        key={providerIndex}
                        className="flex items-center cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={procedure.providerNames?.includes(provider.providerName)}
                          onChange={() => toggleProvider(index, provider.providerName)}
                          className="mr-2"
                        />
                        <span className="text-sm">{provider.providerName}</span>
                      </label>
                    )
                  ))}
                </div>
              </div>
            )}

            {procedures.length > 1 && (
              <button
                onClick={() => removeProcedure(index)}
                className="text-red-500 hover:text-red-700 text-sm transition-colors"
              >
                ✕ Remove
              </button>
            )}
          </div>
        );
        })}
      </div>

      <button
        onClick={addProcedure}
        className="w-full py-3 border-2 border-dashed border-border rounded-lg text-primary hover:border-primary transition-all mb-6"
      >
        + Add Another Procedure
      </button>
        </>
      )}

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t border-border">
        <button
          onClick={handleSkip}
          className="text-text hover:text-dark transition-colors"
        >
          Skip this step
        </button>

        <button
          onClick={handleContinue}
          className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-opacity-90 transition-all font-medium"
        >
          Continue →
        </button>
      </div>
    </div>
  );
};

export default Procedures;

